import _MenuList from "@mui/material/MenuList";
var _MenuIcon, _SupportMenuIcon, _SovosDivider;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useCallback, useMemo, useState } from 'react';
import { bool, arrayOf, func, number, object, oneOfType, shape, string, elementType } from 'prop-types';
import { v4 as uuid } from 'uuid';
import Popover, { popoverClasses } from '@mui/material/Popover';
import Button, { buttonClasses } from '@mui/material/Button';
import { menuItemClasses } from '@mui/material/MenuItem';
import { dividerClasses } from '@mui/material/Divider';
import { alpha, styled } from '@mui/material/styles';
import { Apps, SovosLogo, Support } from 'mosaic-react-icons';
import BaseIcon from './components/BaseIcon';
import MenuIcon from './components/MenuIcon';
import SovosTooltip from '../sovos-tooltip';
import SovosMenuItem from '../sovos-menu-item';
import { square } from '../internals/utils/stylesHelpers';
import useMosaicTranslation from '../internals/i18n/useMosaicTranslation';
import SovosDivider from '../sovos-divider';
import SupportMenuIcon from './components/SupportMenuIcon';
export const productSwitcherClasses = {
  buttonContents: 'sovosProductSwitcher__buttonContents'
};
const Root = styled('div')({
  flex: '0 0 auto'
});
const ToggleButton = styled(Button)(_ref => {
  let {
    theme: {
      palette
    }
  } = _ref;
  return {
    borderRadius: 0,
    minWidth: 0,
    padding: 0,
    textTransform: 'none',
    width: 'auto',
    ["&.".concat(buttonClasses.focusVisible)]: {
      color: alpha(palette.primary.contrastText, 0.4)
    }
  };
});
const ProductItem = styled(SovosMenuItem)(_ref2 => {
  let {
    theme: {
      spacing
    }
  } = _ref2;
  return {
    gap: spacing(1.5),
    height: spacing(6),
    ["&.".concat(menuItemClasses.root)]: {
      ["&+.".concat(dividerClasses.root)]: {
        margin: "".concat(spacing(1), " ").concat(spacing(2))
      }
    }
  };
});
const ProductItemName = styled('span')(_ref3 => {
  let {
    theme: {
      typography
    }
  } = _ref3;
  return {
    ...typography.h6,
    fontWeight: '500'
  };
});
const SupportItem = styled(SovosMenuItem)(_ref4 => {
  let {
    theme: {
      spacing
    }
  } = _ref4;
  return {
    gap: spacing(1.5),
    height: spacing(5)
  };
});
const SupportItemLinkText = styled('span')(_ref5 => {
  let {
    theme: {
      palette
    }
  } = _ref5;
  return {
    color: palette.primary.main,
    '&:hover': {
      textDecoration: 'underline'
    }
  };
});
const Menu = styled(_MenuList)({
  padding: 0
});
const MenuPopover = styled(Popover)(_ref6 => {
  let {
    theme: {
      spacing
    }
  } = _ref6;
  return {
    ["& .".concat(popoverClasses.paper)]: {
      minWidth: spacing(50),
      paddingTop: spacing(1),
      paddingBottom: spacing(1)
    }
  };
});
const SovosLogoBlue = styled(SovosLogo)(_ref7 => {
  let {
    theme: {
      palette
    }
  } = _ref7;
  return {
    '& path': {
      fill: palette.primary.main
    }
  };
});
const LogoUrlButtonIcon = styled('div', {
  shouldForwardProp: prop => prop !== 'isVisible' && prop !== 'logoUrl'
})(_ref8 => {
  let {
    isVisible,
    logoUrl,
    theme: {
      spacing
    }
  } = _ref8;
  return {
    backgroundImage: isVisible ? "url(\"".concat(logoUrl, "\")") : undefined,
    backgroundRepeat: 'no-repeat',
    backgroundSize: "".concat(spacing(3), " ").concat(spacing(3)),
    pointerEvents: 'none',
    ...square(spacing(3))
  };
});
const ProductLogoUrlMenuIcon = styled('div', {
  shouldForwardProp: prop => prop !== 'logoUrl'
})(_ref9 => {
  let {
    logoUrl,
    theme: {
      spacing
    }
  } = _ref9;
  return {
    backgroundImage: "url(\"".concat(logoUrl, "\")"),
    backgroundRepeat: 'no-repeat',
    backgroundSize: "".concat(spacing(3.5), " ").concat(spacing(3.5)),
    margin: spacing(0.25),
    ...square(spacing(3.5))
  };
});
const SupportIconUrlMenuIcon = styled('div', {
  shouldForwardProp: prop => prop !== 'iconUrl'
})(_ref10 => {
  let {
    iconUrl,
    theme: {
      spacing
    }
  } = _ref10;
  return {
    backgroundImage: "url(\"".concat(iconUrl, "\")"),
    backgroundRepeat: 'no-repeat',
    backgroundSize: "".concat(spacing(3), " ").concat(spacing(3)),
    ...square(spacing(3))
  };
});
const ButtonContents = styled('div', {})(_ref11 => {
  let {
    theme: {
      palette,
      shape: themeShape,
      spacing
    }
  } = _ref11;
  return {
    alignItems: 'center',
    background: "linear-gradient(45deg, ".concat(palette.primary.dark, ", ").concat(palette.primary.main, ")"),
    borderRadius: themeShape.borderRadius,
    display: 'flex',
    justifyContent: 'center',
    marginInlineEnd: 0,
    ...square(spacing(5))
  };
});
const SovosProductMenu = _ref12 => {
  var _ToggleButton;
  let {
    onClose,
    onSelectProduct,
    PopoverProps,
    products,
    selectedProduct,
    supportItems,
    tooltipText,
    ...rest
  } = _ref12;
  const {
    t
  } = useMosaicTranslation();
  const [hovered, setHovered] = useState(false);
  const showAppsIcon = hovered;
  const selectedItem = useMemo(() => ({
    id: selectedProduct.id || selectedProduct.name,
    ...selectedProduct
  }), [selectedProduct]);
  const productItems = useMemo(() => products.map(p => {
    const item = {
      ...p
    };
    if (!item.id) {
      item.id = p.name || uuid();
    }
    return item;
  }), [products]);
  const productIcon = () => {
    if (selectedProduct.icon) {
      return /*#__PURE__*/React.createElement(BaseIcon, {
        element: selectedProduct.icon,
        isVisible: !showAppsIcon
      });
    }
    if (selectedProduct.logoMonoUrl || selectedProduct.logoUrl) {
      return /*#__PURE__*/React.createElement(LogoUrlButtonIcon, {
        isVisible: !showAppsIcon,
        logoUrl: selectedProduct.logoMonoUrl || selectedProduct.logoUrl
      });
    }
    return /*#__PURE__*/React.createElement(BaseIcon, {
      element: SovosLogo,
      isVisible: !showAppsIcon
    });
  };
  const buttonContents = /*#__PURE__*/React.createElement(ButtonContents, {
    className: productSwitcherClasses.buttonContents,
    onPointerOver: () => setHovered(true),
    onPointerOut: () => setHovered(false)
  }, /*#__PURE__*/React.createElement(BaseIcon, {
    element: Apps,
    isVisible: showAppsIcon
  }), productIcon());
  const [anchorEl, setAnchorEl] = useState(null);
  const handleButtonClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = useCallback(() => {
    setAnchorEl(null);
    if (onClose) {
      onClose();
    }
  }, [onClose]);
  const handleSelectItem = useCallback(item => {
    if (selectedItem.id !== item.id && onSelectProduct) {
      onSelectProduct(item);
    }
    handleClose(null);
  }, [handleClose, onSelectProduct, selectedItem]);
  const renderMenuIcon = product => {
    if (product.icon) {
      return /*#__PURE__*/React.createElement(MenuIcon, {
        element: product.icon
      });
    }
    if (product.logoUrl) {
      return /*#__PURE__*/React.createElement(ProductLogoUrlMenuIcon, {
        logoUrl: product.logoUrl
      });
    }
    return _MenuIcon || (_MenuIcon = /*#__PURE__*/React.createElement(MenuIcon, {
      element: SovosLogoBlue
    }));
  };
  const renderProductItems = () => productItems.map(item => /*#__PURE__*/React.createElement(ProductItem, {
    disabled: item.disabled,
    key: item.id,
    onClick: () => handleSelectItem(item),
    selected: selectedItem.id === item.id
  }, renderMenuIcon(item), /*#__PURE__*/React.createElement(ProductItemName, null, item.name)));
  const renderSupportMenuIcon = supportItem => {
    if (supportItem.icon) {
      return /*#__PURE__*/React.createElement(SupportMenuIcon, {
        element: supportItem.icon
      });
    }
    if (supportItem.iconUrl) {
      return /*#__PURE__*/React.createElement(SupportIconUrlMenuIcon, {
        iconUrl: supportItem.iconUrl
      });
    }
    return _SupportMenuIcon || (_SupportMenuIcon = /*#__PURE__*/React.createElement(SupportMenuIcon, {
      element: Support
    }));
  };
  const renderSupportItems = () => {
    if (!supportItems || !supportItems.length) {
      return undefined;
    }
    return [_SovosDivider || (_SovosDivider = /*#__PURE__*/React.createElement(SovosDivider, {
      key: "divider"
    })), ...supportItems.map(item => /*#__PURE__*/React.createElement(SupportItem, {
      component: "a",
      disabled: item.disabled,
      href: item.url,
      key: item.linkText,
      onClick: handleClose,
      target: "_blank"
    }, renderSupportMenuIcon(item), /*#__PURE__*/React.createElement("span", null, item.additionalText, "\xA0", /*#__PURE__*/React.createElement(SupportItemLinkText, null, item.linkText))))];
  };
  const renderPopover = () => /*#__PURE__*/React.createElement(MenuPopover, _extends({
    anchorOrigin: {
      horizontal: 0,
      vertical: 'bottom'
    },
    anchorEl: anchorEl,
    className: "sovosProductMenu__popover",
    "data-testid": "sovosProductMenu__popover",
    marginThreshold: 0,
    onClose: handleClose,
    open: !!anchorEl,
    transformOrigin: {
      horizontal: 'left',
      vertical: 'top'
    }
  }, PopoverProps), /*#__PURE__*/React.createElement(Menu, null, renderProductItems(), renderSupportItems()));
  const renderButton = () => {
    const button = _ToggleButton || (_ToggleButton = /*#__PURE__*/React.createElement(ToggleButton, {
      className: "sovosProductMenu__button",
      "data-testid": "sovosProductMenu__button",
      fullWidth: true,
      onClick: handleButtonClick
    }, buttonContents));
    return /*#__PURE__*/React.createElement(SovosTooltip, {
      title: tooltipText !== null && tooltipText !== void 0 ? tooltipText : t('navigation.productSwitcherLabel')
    }, button);
  };
  return /*#__PURE__*/React.createElement(Root, _extends({
    className: "sovosProductMenu"
  }, rest), renderButton(), renderPopover());
};
SovosProductMenu.propTypes = {
  /**
   * Callback fired when the menu is closed
   */
  onClose: func,
  /**
   * Callback fired when a product is selected from the menu
   */
  onSelectProduct: func,
  /**
   * Properties for the menu popover
   */
  PopoverProps: object,
  /**
   * Array of objects, each with
   * - `icon` element. Icon rendered in the toggle button and menu
   * - `id` string or number. Required if there is any chance the names may
   *   not be unique.
   * - `logoUrl` string. Background image url for the toggle button and menu
   * - `name` string
   * - `disabled` bool
   */
  products: arrayOf(shape({
    icon: elementType,
    id: oneOfType([number, string]),
    logoMonoUrl: string,
    logoUrl: string,
    name: string,
    disabled: bool
  })).isRequired,
  /**
   * The currently selected menu item. Has the same properties as objects in the products array
   */
  selectedProduct: shape({
    icon: elementType,
    id: oneOfType([number, string]),
    logoMonoUrl: string,
    logoUrl: string,
    name: string,
    disabled: bool
  }),
  /**
   * Array of objects, each with
   * - `additionalText` string
   * - `disabled` bool
   * - `icon` element
   * - `linkText` string
   * - `target` string
   * - `url` string
   */
  supportItems: arrayOf(shape({
    additionalText: string,
    disabled: bool,
    icon: elementType,
    iconUrl: string,
    linkText: string.isRequired,
    url: string.isRequired
  })),
  /**
   * Tooltip for the menu toggle button
   */
  tooltipText: string
};
export default SovosProductMenu;